import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";

const cardStyles = cva(
  "rounded-2xl border bg-surface transition-colors duration-200",
  {
    variants: {
      variant: {
        default: "border-stroke",
        subtle: "border-stroke/50 bg-bg-raised",
        interactive: [
          "border-stroke cursor-pointer",
          "hover:-translate-y-0.5 hover:border-stroke-strong",
          "hover:shadow-[0_16px_36px_-18px_rgba(0,0,0,0.65)]",
          "transition-transform duration-200",
        ].join(" "),
        ornate: [
          "border-amber/22",
          "bg-[linear-gradient(135deg,rgba(245,178,99,0.1),rgba(255,122,89,0.04)_60%,transparent),var(--surface)]",
        ].join(" "),
      },
      padding: {
        none: "p-0",
        sm: "p-4",
        md: "p-5",
        lg: "p-6",
      },
    },
    defaultVariants: {
      variant: "default",
      padding: "md",
    },
  },
);

type CardProps = HTMLAttributes<HTMLDivElement> & VariantProps<typeof cardStyles>;

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, padding, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardStyles({ variant, padding }), className)}
      {...props}
    />
  ),
);
Card.displayName = "Card";

type CardHeaderProps = HTMLAttributes<HTMLDivElement> & {
  title?: string;
  subtitle?: string;
};

export function CardHeader({ title, subtitle, className, children, ...props }: CardHeaderProps) {
  return (
    <div className={cn("mb-3 flex items-center justify-between", className)} {...props}>
      <div>
        {title && (
          <h3 className="font-serif font-semibold text-lg tracking-tight">{title}</h3>
        )}
        {subtitle && (
          <p className="mt-0.5 text-xs uppercase tracking-[0.18em] text-ink-dim">
            {subtitle}
          </p>
        )}
      </div>
      {children}
    </div>
  );
}
